import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getCourierOffice,
  calculateCourierFee,
  viewCourierFeeConfig,
  configureCourierFee,
  createCourierOffice,
  viewCurrier,
  getAllCurrier,
  createCurrier,
  getCurrierAgents,
  deleteCurrier,
  editCurrier,
  editCourierOffice,
  toggleCourierOFficeStatus,
} from "../../apis/courier";
import { toast } from "react-toastify";

// Types
interface CourierState {
  courierOffices: any;
  couriers: any;
  agents: any;
  feeConfig: any | null;
  currentCourier: any | null; // For storing a single courier
  loading: boolean;
  error: string | null;
}

// Initial State
const initialState: CourierState = {
  courierOffices: [],
  couriers: [],
  agents: [],
  feeConfig: null,
  currentCourier: null, // Initialize as null
  loading: false,
  error: null,
};

// Async Thunks
export const fetchCourierOffice = createAsyncThunk(
  "courier/fetchCourierOffice",
  async (parameters: any, { rejectWithValue }) => {
    try {
      return await getCourierOffice(parameters);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message ?? error.message);
    }
  }
);

export const fetchCourierFee = createAsyncThunk(
  "courier/fetchCourierFee",
  async (feeDetails: any, { rejectWithValue }) => {
    try {
      return await calculateCourierFee(feeDetails);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message ?? error.message);
    }
  }
);

export const fetchCourierFeeConfig = createAsyncThunk(
  "courier/fetchCourierFeeConfig",
  async (feeId: any, { rejectWithValue }) => {
    try {
      return await viewCourierFeeConfig(feeId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message ?? error.message);
    }
  }
);

export const updateCourierFeeConfig = createAsyncThunk(
  "courier/updateCourierFeeConfig",
  async (feeDetails: any, { rejectWithValue }) => {
    try {
      return await configureCourierFee(feeDetails);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message ?? error.message);
    }
  }
);

export const addCourierOffice = createAsyncThunk(
  "courier/addCourierOffice",
  async (officeDetails: any, { rejectWithValue }) => {
    try {
      return await createCourierOffice(officeDetails);
    } catch (error: any) {
      toast.error(error.response?.data?.message?.message ?? error.message);
      return rejectWithValue(error.response?.data?.message ?? error.message);
    }
  }
);

export const editCourierOfficeAsync = createAsyncThunk(
  "courier/editCourierOffice",
  async (parameters: any, { rejectWithValue }) => {
    try {
      return await editCourierOffice(parameters);
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? error.message);
      return rejectWithValue(error.response?.data?.message ?? error.message);
    }
  }
);

export const toggleCourierOfficeStatusAsync = createAsyncThunk(
  "courier/toggleCourierOfficeStatus",
  async ({ branchId, isEnabled }: any, { rejectWithValue }) => {
    try {
      return await toggleCourierOFficeStatus(branchId,isEnabled);
    } catch (error: any) {
      toast.error(error.response?.data?.message?.message ?? error.message);
      return rejectWithValue(error.response?.data?.message ?? error.message);
    }
  }
);

export const fetchCourier = createAsyncThunk(
  "courier/fetchCourier",
  async (courierId: any, { rejectWithValue }) => {
    try {
      return await viewCurrier(courierId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message ?? error.message);
    }
  }
);

export const fetchAllCouriers = createAsyncThunk(
  "courier/fetchAllCouriers",
  async (parameters: any, { rejectWithValue }) => {
    try {
      const res = await getAllCurrier(parameters);
      return res;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message ?? error.message);
    }
  }
);

export const addCourier = createAsyncThunk(
  "courier/addCourier",
  async (currierDetails: any, { rejectWithValue }) => {
    try {
      return await createCurrier(currierDetails);
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? error.message);
      return rejectWithValue(error.response?.data?.message ?? error.message);
    }
  }
);

export const fetchCourierAgents = createAsyncThunk(
  "courier/fetchCourierAgents",
  async (parameters: any, { rejectWithValue }) => {
    try {
      return await getCurrierAgents(parameters);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message ?? error.message);
    }
  }
);

export const deleteCourier = createAsyncThunk(
  "courier/deleteCourier",
  async (courierId: any, { rejectWithValue }) => {
    try {
      return await deleteCurrier(courierId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message ?? error.message);
    }
  }
);

export const editCourier = createAsyncThunk(
  "courier/editCourier",
  async (
    { courierId, currierDetails }: { courierId: any; currierDetails: any },
    { rejectWithValue }
  ) => {
    try {
      return await editCurrier(courierId, currierDetails);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message ?? error.message);
    }
  }
);

// Slice
const courierSlice = createSlice({
  name: "courier",
  initialState,
  reducers: {
    resetCourier: (state) => {
      state.courierOffices = [];
      state.couriers = [];
      state.agents = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Courier Office
      .addCase(fetchCourierOffice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCourierOffice.fulfilled, (state, action) => {
        state.loading = false;
        state.courierOffices = action.payload;
      })
      .addCase(fetchCourierOffice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch All Couriers
      .addCase(fetchAllCouriers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllCouriers.fulfilled, (state, action) => {
        state.loading = false;
        state.couriers = action.payload;
      })
      .addCase(fetchAllCouriers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Add Courier Office
      .addCase(addCourierOffice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addCourierOffice.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(addCourierOffice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(editCourierOfficeAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editCourierOfficeAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(editCourierOfficeAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(toggleCourierOfficeStatusAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleCourierOfficeStatusAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(toggleCourierOfficeStatusAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Add Courier
      .addCase(addCourier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addCourier.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addCourier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update Courier Fee Config
      .addCase(updateCourierFeeConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCourierFeeConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.feeConfig = action.payload;
      })
      .addCase(updateCourierFeeConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Single Courier
      .addCase(fetchCourier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCourier.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCourier = action.payload;
      })
      .addCase(fetchCourier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Courier Agents
      .addCase(fetchCourierAgents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCourierAgents.fulfilled, (state, action) => {
        state.loading = false;
        state.agents = action.payload;
      })
      .addCase(fetchCourierAgents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete Courier
      .addCase(deleteCourier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCourier.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteCourier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Edit Courier
      .addCase(editCourier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editCourier.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(editCourier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetCourier } = courierSlice.actions;

export default courierSlice.reducer;
